import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import NativeLanguageSelect from "@/components/NativeLanguageSelect";
import { useAuth } from "@/lib/auth";
import { languageDisplayName } from "@/lib/flashcardLabels";
import { useUpdateStudentNativeLanguage, type StudentL1Status } from "@/lib/languages";

interface NativeLanguagePromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // What the server would use as the L1 today, and why it is asking. Null while
  // the status query is still in flight — the dialog renders nothing then.
  status: StudentL1Status | null | undefined;
  // Fired after the answer is saved, so the caller can retry the capture or the
  // deck creation that was refused. The student's word is never lost.
  onAnswered?: () => void;
}

// The question nobody was ever asked (#878). A student whose native language
// was guessed from their interface locale, where that guess is a language they
// are LEARNING, has every capture in it refused and its deck refused with it.
// The server says NEEDS_NATIVE_LANGUAGE rather than skipping silently; this is
// where the student answers.
//
// Always dismissible. A student whose home language genuinely IS the language
// they are learning cannot be stored — every writer of native_language refuses
// that pair — so the dialog must never become a wall they cannot pass. Closing
// it returns them to exactly today's behaviour, which is the floor this change
// guarantees.
export default function NativeLanguagePrompt({ open, onOpenChange, status, onAnswered }: NativeLanguagePromptProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const update = useUpdateStudentNativeLanguage();
  const [languageId, setLanguageId] = useState("");

  const learning = user?.studentProfile?.studentLanguages ?? [];
  const guessed = status?.language;
  const guessedName = guessed ? languageDisplayName(guessed.code, guessed.name, i18n.language) : "";

  // The copy names the guessed language, so there is nothing worth showing
  // until the status query lands. Callers may flip `open` first and let the
  // fetch follow.
  if (!guessed) return null;

  const submit = () => {
    if (!languageId || update.isPending) return;
    update.mutate(languageId, {
      onSuccess: (data) => {
        const payload = data.updateStudentNativeLanguage;
        if (payload.errors.length > 0 || !payload.studentProfile) {
          toast.error(t("flashcards.nativePrompt.errors.failed"));
          return;
        }
        toast.success(t("flashcards.nativePrompt.saved"));
        onOpenChange(false);
        onAnswered?.();
      },
      onError: () => toast.error(t("flashcards.nativePrompt.errors.failed")),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("flashcards.nativePrompt.title")}</DialogTitle>
          <DialogDescription>
            {t("flashcards.nativePrompt.description", { language: guessedName })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="native-language-prompt">{t("flashcards.nativePrompt.label")}</Label>
          <NativeLanguageSelect
            id="native-language-prompt"
            value={languageId}
            onChange={setLanguageId}
            disabled={update.isPending}
            // The server refuses a language they are learning, so never offer one.
            exclude={learning.map((sl) => sl.language.id)}
          />
          {/* The honest other branch. If the guess is right, the student is
              signed up to learn a language they already speak — there is no
              card to make, and no way for them to record that answer. Say so
              plainly rather than leaving them to keep trying. */}
          <p data-testid="native-prompt-really" className="text-xs text-muted-foreground">
            {t("flashcards.nativePrompt.reallyMine", { language: guessedName })}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("flashcards.nativePrompt.notNow")}
          </Button>
          <Button onClick={submit} disabled={!languageId || update.isPending}>
            {update.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("flashcards.nativePrompt.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
